import { useState, useEffect } from 'react';
import apiClient from '@/lib/api/client';

interface Category {
  id: number;
  name: string;
  slug: string | null;
  parent_id: number | null;
  product_count?: number;
  children?: Category[];
}

interface CatalogMenuProps {
  onCategorySelect?: (path: string[]) => void;
  selectedCategory?: string[];
}

// Names that indicate a lumber-related category
const LUMBER_NAMES = new Set([
  'lumber & composites', 'dimensional lumber', 'studs', 'treated lumber',
  'plywood', 'osb', 'mdf', 'particle board', 'specialty wood', 'sheet goods',
  'all lumber', 'framing lumber', 'lumber',
]);

function isLumberCat(name: string) {
  return LUMBER_NAMES.has(name.toLowerCase());
}

export const CatalogMenu = ({ onCategorySelect, selectedCategory = [] }: CatalogMenuProps) => {
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadCategories(); }, []);

  const loadCategories = async () => {
    try {
      const response = await apiClient.get('/catalog/categories');
      const flat: Category[] = response.data || [];
      // Build tree
      const map = new Map<number, Category>();
      flat.forEach(c => map.set(c.id, { ...c, children: [] }));
      const roots: Category[] = [];
      map.forEach(c => {
        if (c.parent_id === null) roots.push(c);
        else map.get(c.parent_id)?.children!.push(c);
      });
      const sort = (cs: Category[]) => {
        cs.sort((a, b) => a.name.localeCompare(b.name));
        cs.forEach(c => c.children?.length && sort(c.children));
      };
      sort(roots);
      setAllCategories(flat.map(c => map.get(c.id)!).filter(Boolean));
    } catch (e) {
      console.error('Failed to load categories:', e);
    } finally {
      setLoading(false);
    }
  };

  const select = (path: string[]) => onCategorySelect?.(path);
  const activeLeaf = selectedCategory[selectedCategory.length - 1]?.toLowerCase() ?? '';

  // ── Lumber tree ──────────────────────────────────────────────────────────
  // Find the "Lumber & Composites" parent and its children; also grab flat root lumber cats
  const lumberParent = allCategories.find(c => c.name.toLowerCase() === 'lumber & composites');
  const lumberChildren = lumberParent?.children ?? [];

  // Flat top-level lumber categories (All Lumber, Framing Lumber, etc.)
  const flatLumberRoots = allCategories.filter(
    c => c.parent_id === null && isLumberCat(c.name) && c.name.toLowerCase() !== 'lumber & composites'
  );

  // Combine: flat roots first, then subcategories of Lumber & Composites
  const lumberItems = [
    ...flatLumberRoots.sort((a, b) => a.name.localeCompare(b.name)),
    ...lumberChildren.sort((a, b) => a.name.localeCompare(b.name)),
  ];

  // ── Full tree (non-lumber) ────────────────────────────────────────────────
  const rootCategories = allCategories.filter(
    c => c.parent_id === null && !isLumberCat(c.name)
  );

  const renderCategory = (cat: Category, parentPath: string[] = [], depth = 0): JSX.Element => {
    const path = [...parentPath, cat.name];
    const active = cat.name.toLowerCase() === activeLeaf;
    const hasChildren = (cat.children?.length ?? 0) > 0;

    if (!hasChildren) {
      return (
        <li key={cat.id}>
          <button
            onClick={() => select(path)}
            className={`flex items-center justify-between w-full rounded px-2 py-1 text-left hover:bg-slate-100 transition-colors ${
              active ? 'bg-primary/10 text-primary font-semibold' : 'text-slate-600'
            } ${depth === 0 ? 'text-sm font-medium' : 'text-xs'}`}
          >
            <span>{cat.name}</span>
            {(cat.product_count ?? 0) > 0 && (
              <span className="text-[10px] text-slate-400 ml-1">{cat.product_count}</span>
            )}
          </button>
        </li>
      );
    }

    return (
      <li key={cat.id}>
        <details className="group" open={depth === 0}>
          <summary className={`flex items-center justify-between cursor-pointer rounded-lg px-3 py-1.5 hover:bg-slate-100 transition-colors ${
            active ? 'text-primary font-semibold' : depth === 0 ? 'text-sm font-medium text-slate-700' : 'text-sm text-slate-600'
          }`}>
            <span onClick={e => { e.stopPropagation(); select(path); }} className="cursor-pointer hover:underline">
              {cat.name}
            </span>
            <span className="material-symbols-outlined text-xs transition-transform group-open:rotate-90">chevron_right</span>
          </summary>
          <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-slate-200 pl-3">
            {cat.children!.map(child => renderCategory(child, path, depth + 1))}
          </ul>
        </details>
      </li>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <h2 className="px-3 text-lg font-bold text-slate-900">Catalog</h2>
        <p className="px-3 text-sm text-slate-400">Loading…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-4 gap-5">
      <h2 className="px-1 text-lg font-bold text-slate-900">Catalog</h2>

      {/* All items */}
      <button
        onClick={() => select([])}
        className={`text-left px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:bg-slate-100 ${
          selectedCategory.length === 0 ? 'bg-primary/10 text-primary font-semibold' : 'text-slate-600'
        }`}
      >
        All Items
      </button>

      {/* ── Lumber Filter Tree ── */}
      {lumberItems.length > 0 && (
        <div>
          <p className="px-1 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Lumber
          </p>
          <ul className="space-y-0.5">
            {lumberItems.map(cat => {
              const path = [cat.name];
              const active = cat.name.toLowerCase() === activeLeaf;
              const hasChildren = (cat.children?.length ?? 0) > 0;

              if (!hasChildren) {
                return (
                  <li key={cat.id}>
                    <button
                      onClick={() => select(path)}
                      className={`flex items-center justify-between w-full rounded-lg px-3 py-1.5 text-left text-sm transition-colors hover:bg-slate-100 ${
                        active ? 'bg-primary/10 text-primary font-semibold' : 'text-slate-600'
                      }`}
                    >
                      <span>{cat.name}</span>
                      {(cat.product_count ?? 0) > 0 && (
                        <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1">{cat.product_count}</span>
                      )}
                    </button>
                  </li>
                );
              }

              // Expandable with children (e.g. Lumber & Composites sub-nodes)
              return (
                <li key={cat.id}>
                  <details open>
                    <summary className={`flex items-center justify-between cursor-pointer rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-slate-100 transition-colors list-none ${
                      active ? 'text-primary' : 'text-slate-700'
                    }`}>
                      <span onClick={e => { e.stopPropagation(); select(path); }} className="cursor-pointer hover:underline">
                        {cat.name}
                      </span>
                      <span className="material-symbols-outlined text-xs text-slate-400">expand_more</span>
                    </summary>
                    <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-slate-200 pl-3">
                      {cat.children!.map(child => {
                        const childPath = [...path, child.name];
                        const childActive = child.name.toLowerCase() === activeLeaf;
                        return (
                          <li key={child.id}>
                            <button
                              onClick={() => select(childPath)}
                              className={`flex items-center justify-between w-full rounded px-2 py-1 text-left text-xs transition-colors hover:bg-slate-100 ${
                                childActive ? 'bg-primary/10 text-primary font-semibold' : 'text-slate-600'
                              }`}
                            >
                              <span>{child.name}</span>
                              {(child.product_count ?? 0) > 0 && (
                                <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1">{child.product_count}</span>
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ── Full Category Tree ── */}
      <div>
        <p className="px-1 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
          All Categories
        </p>
        <ul className="space-y-0.5">
          {rootCategories.map(cat => renderCategory(cat, [], 0))}
        </ul>
      </div>
    </div>
  );
};
